import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Eye, X, Plus, Trash2, Pencil, Upload, Download } from "lucide-react";
import { API } from "@/context/AuthContext";
import { HOLIDAY_LABELS, HOLIDAY_ORDER, nextOccurrenceLabel } from "@/lib/holidays";

const DEFAULT_GREETINGS = {
  weihnachten: "Wir wünschen all unseren Kunden ein frohes Weihnachtsfest und besinnliche Feiertage.",
  karfreitag: "Wir gedenken heute dem Sterben Jesu.",
  ostern: "Frohe Ostern – wir wünschen unserer Kundschaft erholsame Feiertage.",
  silvester: "Einen guten Rutsch und ein erfolgreiches neues Jahr wünscht das Team von Hamm EP.",
  tag_der_deutschen_einheit: "Tag der Deutschen Einheit – wir feiern das gemeinsame Deutschland.",
  black_friday: "Black Friday – exklusive B2B-Konditionen auf Anfrage.",
  weltfrauentag: "Anlässlich des Weltfrauentags danken wir für den Einsatz aller Frauen in Wirtschaft und Gesellschaft.",
  valentinstag: "Zum Valentinstag – herzliche Grüße an unsere Kundschaft und Partnerbetriebe.",
};

const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#D9E0E6] bg-white text-sm text-[#1d1d1b] focus:outline-none focus:ring-2 focus:ring-[#00AAA0] focus:border-[#00AAA0]";
const labelCls = "block font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b] mb-2";
const EMPTY_CE = { name: "", greeting: "", start_date: "", end_date: "", is_active: true, image_url: "" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absoluteUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

async function uploadMedia(file) {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await axios.post(`${API}/holiday-media/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
  return data; // { url, filename }
}

function MediaBlock({ imageUrl, onUploaded, onRemove, testidPrefix }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const abs = absoluteUrl(imageUrl);
  const download = () => {
    if (!abs) return;
    const a = document.createElement("a");
    a.href = abs; a.download = ""; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const pick = async (f) => {
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|gif|svg\+xml)$/.test(f.type)) { toast.error("Nur JPG/PNG/WEBP/GIF/SVG erlaubt"); return; }
    setBusy(true);
    try { const r = await uploadMedia(f); onUploaded(r.url); toast.success("Datei hochgeladen"); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3" data-testid={`${testidPrefix}-media`}>
      {abs ? (
        <img src={abs} alt="Banner-Grafik" className="w-24 h-12 object-contain rounded-md border border-[#E3E8EC] bg-white" />
      ) : (
        <div className="w-24 h-12 rounded-md border border-dashed border-[#D9E0E6] flex items-center justify-center text-[10px] text-[#1d1d1b]/50">Keine Datei</div>
      )}
      <input ref={fileRef} type="file" hidden accept="image/*,image/svg+xml" onChange={(e) => pick(e.target.files?.[0])} data-testid={`${testidPrefix}-media-file`} />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
        className="flex items-center gap-1.5 px-3 h-8 rounded-md border border-[#2C57D2] text-[#2C57D2] hover:bg-[#2C57D2] hover:text-white font-tech text-[10px] tracking-[0.18em] uppercase transition-colors disabled:opacity-60" data-testid={`${testidPrefix}-media-upload`}>
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Upload
      </button>
      <button type="button" onClick={download} disabled={!abs}
        className="flex items-center gap-1.5 px-3 h-8 rounded-md border border-[#E3E8EC] text-[#1d1d1b] hover:border-[#00AAA0] hover:text-[#00AAA0] font-tech text-[10px] tracking-[0.18em] uppercase transition-colors disabled:opacity-40" data-testid={`${testidPrefix}-media-download`}>
        <Download size={11} /> Download
      </button>
      {abs && (
        <button type="button" onClick={() => onRemove()} className="text-xs text-[#1d1d1b]/60 hover:text-[#EF4444]" data-testid={`${testidPrefix}-media-remove`}>entfernen</button>
      )}
    </div>
  );
}

export default function HolidaysManager() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [previewCustom, setPreviewCustom] = useState(null);
  const [customs, setCustoms] = useState([]);
  const [ceForm, setCeForm] = useState(null); // null = closed, "new" | id = open
  const [ce, setCe] = useState(EMPTY_CE);
  const [ceSaving, setCeSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/settings/holidays`, { withCredentials: true }),
        axios.get(`${API}/custom_events/all`, { withCredentials: true }),
      ]);
      setSettings(a.data); setCustoms(b.data || []);
    } catch { toast.error("Feiertage konnten nicht geladen werden"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async (next) => {
    setSettings(next);
    try { await axios.put(`${API}/settings/holidays`, next, { withCredentials: true }); }
    catch { toast.error("Speichern fehlgeschlagen"); load(); }
  };

  const toggle = async (key) => {
    if (!settings) return;
    const next = { ...settings, [key]: { ...settings[key], enabled: !settings[key]?.enabled } };
    setSaving(key); await saveSettings(next); setSaving(null);
    toast.success(`${HOLIDAY_LABELS[key]}: ${next[key].enabled ? "aktiviert" : "deaktiviert"}`);
  };

  const updateGreeting = (key, val) => setSettings((s) => ({ ...s, [key]: { ...s[key], greeting: val } }));
  const commitGreeting = async (key) => {
    if (!settings) return;
    setSaving(key); await saveSettings(settings); setSaving(null);
    toast.success("Grußtext gespeichert");
  };

  const openCeNew = () => { setCe(EMPTY_CE); setCeForm("new"); };
  const openCeEdit = (e) => { setCe({ name: e.name, greeting: e.greeting, start_date: e.start_date, end_date: e.end_date, is_active: e.is_active }); setCeForm(e.id); };
  const closeCe = () => { setCeForm(null); setCe(EMPTY_CE); };

  const saveCe = async () => {
    if (!ce.name.trim() || !ce.start_date || !ce.end_date) { toast.error("Name, Start- und Enddatum sind Pflicht"); return; }
    if (ce.end_date < ce.start_date) { toast.error("Enddatum liegt vor dem Startdatum"); return; }
    setCeSaving(true);
    try {
      if (ceForm === "new") await axios.post(`${API}/custom_events`, ce, { withCredentials: true });
      else await axios.patch(`${API}/custom_events/${ceForm}`, ce, { withCredentials: true });
      toast.success("Event gespeichert"); closeCe(); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setCeSaving(false); }
  };

  const removeCe = async (id, name) => {
    if (!window.confirm(`Event "${name}" wirklich löschen?`)) return;
    try { await axios.delete(`${API}/custom_events/${id}`, { withCredentials: true }); toast.success("Event gelöscht"); await load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const toggleCe = async (e) => {
    try {
      await axios.patch(`${API}/custom_events/${e.id}`, { ...e, is_active: !e.is_active }, { withCredentials: true });
      await load();
    } catch { toast.error("Status konnte nicht geändert werden"); }
  };

  if (!settings) {
    return <div className="p-12 text-center text-[#1d1d1b]" data-testid="holidays-loading"><Loader2 size={16} className="animate-spin inline mr-2" /> Lade Feiertage…</div>;
  }

  return (
    <div data-testid="holidays-manager">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-[#1d1d1b]">Feiertags-Events</h2>
          <p className="text-sm text-[#1d1d1b]/70 mt-1 max-w-3xl">Grußtexte sind frei editierbar. Aktive Events werden automatisch im Zeitraum (Start 1 Tag vorher, Ende 1 Tag danach) im Banner angezeigt. <strong className="text-[#B91C1C]">Karfreitag</strong> legt einen Milchglas-Effekt über die gesamte Website – auch in der Vorschau testbar.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-14" data-testid="holiday-grid">
        {HOLIDAY_ORDER.map((key) => (
          <div key={key} data-testid={`holiday-card-${key}`} className="border border-[#E3E8EC] rounded-2xl bg-white p-5 flex flex-col">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-tech text-[10px] tracking-[0.22em] uppercase text-[#00AAA0]">Feiertag</p>
                <p className="text-base font-bold text-[#1d1d1b] mt-1">{HOLIDAY_LABELS[key]}</p>
                <p className="text-xs text-[#1d1d1b]/60 font-mono mt-1">Nächster Zeitraum: {nextOccurrenceLabel(key)}</p>
              </div>
              <button onClick={() => toggle(key)} disabled={saving === key}
                data-testid={`holiday-toggle-${key}`} aria-pressed={settings[key]?.enabled}
                className={`shrink-0 relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-200 ${settings[key]?.enabled ? "bg-green-500" : "bg-gray-300"} disabled:opacity-60`}>
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${settings[key]?.enabled ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
            <div className="mt-4">
              <label className={labelCls}>Grußtext</label>
              <textarea rows={2} className={`${inputCls} resize-y`}
                value={settings[key]?.greeting || ""}
                onChange={(e) => updateGreeting(key, e.target.value)}
                onBlur={() => commitGreeting(key)}
                placeholder={DEFAULT_GREETINGS[key]}
                data-testid={`holiday-greeting-${key}`} />
            </div>
            <div className="mt-4">
              <label className={labelCls}>Banner-Grafik / Animation</label>
              <MediaBlock imageUrl={settings[key]?.image_url}
                onUploaded={async (url) => { const next = { ...settings, [key]: { ...settings[key], image_url: url } }; await saveSettings(next); }}
                onRemove={async () => { const next = { ...settings, [key]: { ...settings[key], image_url: "" } }; await saveSettings(next); }}
                testidPrefix={`holiday-${key}`} />
              <p className="mt-2 text-[11px] text-[#1d1d1b]/60">Optional – ersetzt die Standard-Animation im Banner. JPG/PNG/WEBP/GIF/SVG, max. 8 MB.</p>
            </div>
            {key === "karfreitag" && (
              <p className="mt-3 text-[11px] text-[#B91C1C] leading-relaxed">Hinweis: Bei Aktivierung wird die Website ganztags durch einen Milchglas-Gedenk-Screen überlagert.</p>
            )}
            <button onClick={() => setPreviewKey(key)} data-testid={`holiday-preview-${key}`}
              className="mt-5 inline-flex items-center gap-2 self-start px-4 h-9 rounded-md border border-[#2C57D2] text-[#2C57D2] hover:bg-[#2C57D2] hover:text-white font-tech text-[10px] tracking-[0.18em] uppercase transition-colors">
              <Eye size={13} /> Live-Vorschau
            </button>
          </div>
        ))}
      </div>

      {/* ---------- Custom Events ---------- */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#1d1d1b]">Eigene Events</h2>
          <p className="text-sm text-[#1d1d1b]/70 mt-1">Aktionszeiträume, Betriebsferien u. ä. mit fixem Start- und Enddatum.</p>
        </div>
        <button onClick={openCeNew} data-testid="custom-event-new-btn"
          className="flex items-center gap-2 px-5 h-10 rounded-md bg-[#2C57D2] hover:bg-[#00AAA0] text-white font-tech text-[11px] tracking-[0.18em] uppercase transition-colors">
          <Plus size={14} /> Neues Event
        </button>
      </div>

      <div className="border border-[#E3E8EC] rounded-lg overflow-hidden bg-white" data-testid="custom-events-table">
        {customs.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-[#1d1d1b]">Noch keine eigenen Events angelegt.</div>
        ) : customs.map((e, i) => (
          <div key={e.id} className={`grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-6 py-4 items-center ${i < customs.length - 1 ? "border-b border-[#EEF1F4]" : ""}`} data-testid={`custom-event-row-${e.id}`}>
            <div className="md:col-span-4 font-medium text-[#1d1d1b]">{e.name}</div>
            <div className="md:col-span-3 font-mono text-xs text-[#1d1d1b]/70">{e.start_date} → {e.end_date}</div>
            <div className="md:col-span-3 text-xs text-[#1d1d1b]/80 truncate">{e.greeting || "—"}</div>
            <button onClick={() => toggleCe(e)} data-testid={`custom-event-toggle-${e.id}`}
              className={`md:col-span-1 relative inline-flex items-center h-6 w-11 rounded-full transition-colors self-start md:self-center ${e.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${e.is_active ? "translate-x-6" : "translate-x-1"}`} />
            </button>
            <div className="md:col-span-1 flex items-center gap-2 md:justify-end">
              <button onClick={() => setPreviewCustom(e.id)} className="w-9 h-9 flex items-center justify-center rounded-md border border-[#E3E8EC] text-[#1d1d1b] hover:border-[#00AAA0] hover:text-[#00AAA0]" data-testid={`custom-event-preview-${e.id}`}><Eye size={14}/></button>
              <button onClick={() => openCeEdit(e)} className="w-9 h-9 flex items-center justify-center rounded-md border border-[#E3E8EC] text-[#1d1d1b] hover:border-[#2C57D2] hover:text-[#2C57D2]" data-testid={`custom-event-edit-${e.id}`}><Pencil size={14}/></button>
              <button onClick={() => removeCe(e.id, e.name)} className="w-9 h-9 flex items-center justify-center rounded-md border border-[#E3E8EC] text-[#1d1d1b] hover:border-[#EF4444] hover:text-[#EF4444]" data-testid={`custom-event-delete-${e.id}`}><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>

      {ceForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="custom-event-modal">
          <div className="bg-white rounded-lg w-full max-w-2xl my-8 border border-[#E3E8EC]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E3E8EC]">
              <h3 className="font-bold text-[#1d1d1b]">{ceForm === "new" ? "Neues Event anlegen" : "Event bearbeiten"}</h3>
              <button onClick={closeCe} className="w-9 h-9 rounded-md border border-[#E3E8EC] flex items-center justify-center text-[#1d1d1b] hover:text-[#EF4444]" data-testid="custom-event-close"><X size={14}/></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className={labelCls}>Name *</label>
                <input className={inputCls} value={ce.name} onChange={(e) => setCe(f => ({ ...f, name: e.target.value }))} placeholder="z. B. Verkaufsaktion Herbst" data-testid="custom-event-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Startdatum *</label>
                  <input type="date" className={inputCls} value={ce.start_date} onChange={(e) => setCe(f => ({ ...f, start_date: e.target.value }))} data-testid="custom-event-start" />
                </div>
                <div>
                  <label className={labelCls}>Enddatum *</label>
                  <input type="date" className={inputCls} value={ce.end_date} onChange={(e) => setCe(f => ({ ...f, end_date: e.target.value }))} data-testid="custom-event-end" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Banner-Text</label>
                <textarea rows={3} className={`${inputCls} resize-y`} value={ce.greeting} onChange={(e) => setCe(f => ({ ...f, greeting: e.target.value }))} placeholder="Text, der zentriert im Banner steht" data-testid="custom-event-greeting" />
              </div>
              <div>
                <label className={labelCls}>Banner-Grafik / Animation</label>
                <MediaBlock imageUrl={ce.image_url}
                  onUploaded={(url) => setCe(f => ({ ...f, image_url: url }))}
                  onRemove={() => setCe(f => ({ ...f, image_url: "" }))}
                  testidPrefix="custom-event" />
                <p className="mt-2 text-[11px] text-[#1d1d1b]/60">Optional – wird zentriert im Banner neben dem Text angezeigt.</p>
              </div>
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={ce.is_active} onChange={(e) => setCe(f => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-[#00AAA0]" data-testid="custom-event-active" />
                <span className="text-sm text-[#1d1d1b]">Event ist aktiv und wird im Zeitraum angezeigt</span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E3E8EC] bg-[#F7F9FA]">
              <button onClick={closeCe} className="px-5 h-10 rounded-md border border-[#E3E8EC] text-[#1d1d1b] font-tech text-[11px] tracking-[0.18em] uppercase">Abbrechen</button>
              <button onClick={saveCe} disabled={ceSaving} data-testid="custom-event-save"
                className="flex items-center gap-2 px-6 h-10 rounded-md bg-[#2C57D2] hover:bg-[#00AAA0] text-white font-tech text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-60">
                {ceSaving && <Loader2 size={13} className="animate-spin" />} Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {(previewKey || previewCustom) && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col" data-testid="holiday-preview-modal">
          <div className="flex items-center justify-between px-5 h-14 bg-[#1d1d1b] text-white">
            <div className="flex items-center gap-3">
              <span className="font-tech text-[10px] tracking-[0.22em] uppercase text-[#00AAA0]">Live-Vorschau</span>
              <span className="text-sm font-medium">
                {previewKey ? HOLIDAY_LABELS[previewKey] : customs.find(c => c.id === previewCustom)?.name}
              </span>
            </div>
            <button onClick={() => { setPreviewKey(null); setPreviewCustom(null); }} data-testid="holiday-preview-close"
              className="flex items-center gap-2 px-4 h-9 rounded-md border border-white/30 hover:border-[#00AAA0] hover:text-[#00AAA0] font-tech text-[10px] tracking-[0.18em] uppercase transition-colors">
              <X size={13} /> Schließen
            </button>
          </div>
          <iframe title="Vorschau" data-testid="holiday-preview-iframe"
            src={previewKey ? `/?preview_holiday=${previewKey}` : `/?preview_custom=${previewCustom}`}
            className="flex-1 w-full bg-white border-0" />
        </div>
      )}
    </div>
  );
}
